/**
 * AthletePanel - Handles selected athlete details panel
 */

import * as d3 from 'd3';
import { AthleteData } from '../types/athlete.types';
import { formatTime } from '../utils/timeFormatter';
import { getCountryFlag } from '../utils/countryFlags';
import { getPanelPosition, savePanelPosition } from '../utils/storage';
import { FilterService } from '../services/FilterService';
import { DataService } from '../services/DataService';

export class AthletePanel {
    private selectedAthlete: AthleteData | null = null;
    private currentStage: number = 1;
    private filterService: FilterService;
    private dataService: DataService;

    constructor(filterService: FilterService, dataService: DataService) {
        this.filterService = filterService;
        this.dataService = dataService;

        if (window.innerWidth > 768) {
            this.initializeDraggable();
        }
    }

    /**
     * Updates the panel with athlete data
     */
    update(athlete: AthleteData | null, currentAthleteData: AthleteData | null, filteredDataLength: number, stage: number): void {
        this.selectedAthlete = athlete;
        this.currentStage = stage;

        if (!athlete || !currentAthleteData) {
            this.hide();
            return;
        }

        this.show();
        this.renderDetails(currentAthleteData, filteredDataLength);
    }

    private renderDetails(athlete: AthleteData, filteredDataLength: number): void {
        const panel = d3.select('#selected-athlete');
        const countryFlag = getCountryFlag(athlete.Country);
        const raceTime = formatTime(athlete.individualRaceTime || 0);
        const cumulativeTime = formatTime(athlete.stageTime);

        // Calculate distance-only cumulative rank
        const stageData = this.dataService.getStageData(this.currentStage as any);
        const distanceRank = this.filterService.getDistanceOnlyRank(stageData, athlete);

        panel.select('.athlete-details').html(`
            <p><strong>Name:</strong> ${athlete.Name}</p>
            <p><strong>Country:</strong> ${countryFlag} ${athlete.Country}</p>
            <p><strong>Bib:</strong> ${athlete.Bib}</p>
            <p><strong>Category:</strong> ${athlete.Event}</p>
            <p><strong>Distance:</strong> ${athlete.Distance} km</p>
            <p><strong>Age Group:</strong> ${athlete['Age Group']}</p>
            <p><strong>Club:</strong> ${athlete.Club || 'None'}</p>
            <hr style="margin: 1rem 0;">
            <p style="margin-bottom: 0.75rem;">
                <strong>🏃 Race ${this.currentStage} Position:</strong>
                <span class="rank-badge individual">#${athlete.individualRaceRank}</span>
                <span style="color: #888; font-size: 0.9rem;">(${raceTime})</span>
            </p>
            <p style="margin-bottom: 0.75rem;">
                <strong>📊 Overall Cumulative (${athlete.Distance}km):</strong>
                <span class="rank-badge cumulative">#${distanceRank}</span>
                <span style="color: #888; font-size: 0.9rem;">(${cumulativeTime})</span>
            </p>
            <p style="color: #888; font-size: 0.9rem; margin-top: 0.5rem;">
                <strong>Filtered Rank:</strong> ${athlete.filteredRank} of ${filteredDataLength}
            </p>
        `);
    }

    private show(): void {
        d3.select('#selected-athlete').classed('active', true);
    }

    private hide(): void {
        d3.select('#selected-athlete').classed('active', false);
    }

    private initializeDraggable(): void {
        const panel = document.getElementById('selected-athlete');
        const dragHandle = panel?.querySelector('.drag-handle') as HTMLElement;
        const header = panel?.querySelector('h3') as HTMLElement;

        if (!panel || !dragHandle) return;

        let isDragging = false;
        let startX: number, startY: number;
        let currentX: number | null = null;
        let currentY: number | null = null;

        // Restore saved position
        const savedPosition = getPanelPosition();
        if (savedPosition) {
            panel.style.position = 'fixed';
            panel.style.left = savedPosition.x + 'px';
            panel.style.top = savedPosition.y + 'px';
            panel.style.right = 'auto';
            panel.style.transform = 'none';
        }

        const startDrag = (e: MouseEvent) => {
            isDragging = true;
            panel.classList.add('dragging');

            const rect = panel.getBoundingClientRect();
            startX = e.clientX - rect.left;
            startY = e.clientY - rect.top;

            if (currentX === null && currentY === null) {
                panel.style.position = 'fixed';
                panel.style.right = 'auto';
                panel.style.transform = 'none';
                panel.style.left = rect.left + 'px';
                panel.style.top = rect.top + 'px';
            }

            e.preventDefault();
        };

        const drag = (e: MouseEvent) => {
            if (!isDragging) return;

            currentX = e.clientX - startX;
            currentY = e.clientY - startY;

            // Keep panel within viewport bounds
            const maxX = window.innerWidth - panel.offsetWidth - 20;
            const maxY = window.innerHeight - panel.offsetHeight - 20;
            currentX = Math.max(20, Math.min(currentX, maxX));
            currentY = Math.max(20, Math.min(currentY, maxY));

            panel.style.left = currentX + 'px';
            panel.style.top = currentY + 'px';

            e.preventDefault();
        };

        const stopDrag = () => {
            if (isDragging) {
                isDragging = false;
                panel.classList.remove('dragging');

                if (currentX !== null && currentY !== null) {
                    savePanelPosition({ x: currentX, y: currentY });
                }
            }
        };

        dragHandle.addEventListener('mousedown', startDrag);
        header.style.cursor = 'move';
        header.addEventListener('mousedown', startDrag);
        document.addEventListener('mousemove', drag);
        document.addEventListener('mouseup', stopDrag);
    }

    getSelectedAthlete(): AthleteData | null {
        return this.selectedAthlete;
    }
}
